import React, { useRef, useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import "./Commitment.css";

export default function Commitment() {
  const containerRef = useRef(null);
  const [clipX, setClipX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [percentage, setPercentage] = useState(50);
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  // Initialize and handle resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        setClipX(width / 2);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Animate percentage change
  useEffect(() => {
    const controls = animate(count, percentage, {
      duration: 0.5,
      ease: "easeOut"
    });
    return controls.stop;
  }, [percentage, count]);

  // Handle pointer movements
  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let x = clientX - rect.left;
      x = Math.max(0, Math.min(rect.width, x));
      setClipX(x);
      setPercentage(Math.round((x / rect.width) * 100));
    };

    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging]);

  // Handle percentage change via buttons
  const handlePercentageChange = (newPercentage) => {
    if (!containerWidth) return;
    const newX = (containerWidth * newPercentage) / 100;
    setClipX(newX);
    setPercentage(newPercentage);
  };

  return (
    <section className="commitment-section">
      <div className="commitment-container">
        <div className="commitment-grid">
          {/* Image Comparison Section */}
          <div className="comparison-container" ref={containerRef}>
            {/* Before Image (Bottom Layer) */}
            <div className="comparison-image-wrapper">
              <img
                src="./before1.webp"
                alt="Before junk removal"
                className="comparison-image"
                loading="lazy"
              />
              <div className="comparison-label comparison-label--before">Before</div>
            </div>
            
            {/* After Image (Top Layer) */}
            <div 
              className="comparison-image-wrapper comparison-image-wrapper--after"
              style={{ clipPath: `inset(0 0 0 ${clipX}px)` }}
            >
              <img
                src="./after1.webp"
                alt="After junk removal"
                className="comparison-image"
                loading="lazy"
              />
              <div className="comparison-label comparison-label--after">After</div>
            </div>

            {/* Divider Handle */}
            <motion.div
              className="comparison-handle"
              style={{ 
                left: `${clipX}px`,
                scale: dragging ? 1.1 : 1
              }}
              onMouseDown={() => setDragging(true)}
              onTouchStart={() => setDragging(true)}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="comparison-handle-inner">
                <FiChevronLeft className="handle-icon" />
                <FiChevronRight className="handle-icon" />
              </div>
            </motion.div>

            {/* Percentage Indicator */}
            <motion.div 
              className="comparison-indicator"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <motion.span>{rounded}</motion.span>%
            </motion.div>
          </div>

          {/* Content Section */}
          <div className="commitment-content">
            <div className="title-wrapper">
              <motion.h2 
                className="commitment-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                Our Commitment to <span className="highlight">Clean Spaces</span>
              </motion.h2>
              <motion.div 
                className="title-underline"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.2, duration: 0.8, ease: "backOut" }}
              />
            </div>
            
            <div className="commitment-text-group">
              <motion.p 
                className="commitment-text"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                We provide comprehensive junk removal services designed to help you
                clear clutter quickly and efficiently. Whether you're a homeowner,
                business owner, contractor, or property manager, we offer tailored
                solutions to meet your specific needs.
              </motion.p>
              
              <motion.p 
                className="commitment-text"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Our team takes care of all the heavy lifting, sorting, and disposal,
                ensuring that your space is left clean and clutter-free. We focus on
                eco-friendly disposal methods including recycling and donating usable items.
              </motion.p>
            </div>

            {/* Features Grid */}
        

            {/* Percentage Controls */}
     

            <motion.a 
              href="/contact" 
              className="commitment-cta"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              whileHover={{ 
                y: -3,
                boxShadow: "0 10px 25px rgba(28, 75, 65, 0.3)"
              }}
              whileTap={{ scale: 0.98 }}
            >
              Get Your Free Estimate
              <span className="cta-arrow">→</span>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}